import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, LogOut, Building2, User as UserIcon, KeyRound, Loader2, CalendarClock, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth, highestRole, ROLE_STYLE, isPlatformAdmin, isPlatformOrFirmAdmin, isAnyUserAdmin } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import PlatformSettingsModal from "./PlatformSettingsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HORIZONS = [5, 10, 15, 20, 25, 30];
// Slice E5n (2026-02-09) — "Firms", "Users", "Audit" are no longer top-
// level tabs. They live inside the Admin dropdown (see AdminNavItem below).
// Admin sits at the end of the row and is visible to every logged-in user;
// non-admin users see only "None" inside its dropdown.
const BASE_NAV = ["Dashboard", "Association", "Elements", "Projections", "Contributions", "Illustrations", "Support"];
export const ADMIN_SUBSCREENS = ["Firms", "Users", "Audit", "Reports", "EmailLog", "Associations", "Subscriptions"];

export default function TopBar({
  associations,
  associationsLoaded,
  currentAid,
  onSelect,
  devMode,
  onExitDevMode,
  horizon,
  onHorizonChange,
  currentScreen,
  onScreenChange,
  viewingPlanYearId,
  onPickPlanYear,
  currentTier,
  currentPlanYear,
  elementCount,
}) {
  const { user, logout, tenants, currentTenantId, setCurrentTenantId } = useAuth();
  const { settings } = useSettings();
  const role = user ? highestRole(user) : null;
  const roleStyle = role ? ROLE_STYLE[role] : null;

  // 2026-03-03 (Dave) — Compute the visible nav based on the current
  // firm's tier (Basic vs Pro) and the platform-settings switches.
  // Falls back to showing every tab (BASE_NAV) when either signal is
  // still loading so we never hide a tab optimistically. `currentTier`
  // comes from the active association bundle (`tenant_tier`); when the
  // switch for that tier is off, the matching tab is dropped from the
  // rendered row. Add a row here + the paired `<tab>_tab_basic` /
  // `_pro` keys in the backend meta and this context to gate a new tab.
  //
  // 2026-03-09 (Dave · Slice 6) — When the user is viewing a CLOSED
  // (archived / non-active) plan year, mothball the edit-focused tabs
  // (Elements, Association) so the read-only prior-year view can't be
  // mistaken for an editable one. Dashboard / Projections /
  // Contributions / Support remain visible — they're the historical
  // review surfaces.
  const MOTHBALL_ON_CLOSED = new Set(["Elements", "Association"]);
  const visibleNav = useMemo(() => {
    if (!settings) return BASE_NAV;
    const tier = (currentTier || "pro").toLowerCase();
    const suffix = tier === "basic" ? "basic" : "pro";
    const gate = {
      Contributions: `contributions_tab_${suffix}`,
      Elements:      `elements_tab_${suffix}`,
      Projections:   `projections_tab_${suffix}`,
      Illustrations: `illustrations_tab_${suffix}`,
    };
    return BASE_NAV.filter((tab) => {
      if (viewingPlanYearId && MOTHBALL_ON_CLOSED.has(tab)) return false;
      const key = gate[tab];
      if (!key) return true;
      return Number(settings[key] ?? 1) === 1;
    });
  }, [settings, currentTier, viewingPlanYearId]);

  const [settingsOpen, setSettingsOpen] = useState(false);

  // 2026-02-25 (Dave) — Association picker is now a FLAT cross-firm
  // list, grouped by firm. Whenever the user picks an association we
  // also sync `currentTenantId` to that association's tenant_id so any
  // firm-scoped downstream views (Admin screens, etc.) stay coherent.
  useEffect(() => {
    if (!currentAid) return;
    const a = associations.find((x) => x.aid === currentAid);
    if (a && a.tenant_id && a.tenant_id !== currentTenantId) {
      setCurrentTenantId(a.tenant_id);
    }
  }, [currentAid, associations, currentTenantId, setCurrentTenantId]);

  // 2026-02-23 (Dave) — Admin dropdown replaced by tile grid on the
  // Admin landing. The Settings tile lives there instead of in a
  // dropdown menu. Since the modal state is owned here in TopBar, the
  // tile fires a lightweight custom DOM event and TopBar opens the
  // modal in response — avoids threading `onOpenSettings` through
  // App.js → AdminScreen.
  useEffect(() => {
    const h = () => setSettingsOpen(true);
    window.addEventListener("ureserve:open-platform-settings", h);
    return () =>
      window.removeEventListener("ureserve:open-platform-settings", h);
  }, []);

  // 2026-02-25 (Dave) — Progressive association picker: flat list of
  // EVERY association the user is credentialed for, regardless of
  // firm. Grouped by firm with the firm name as a section header,
  // associations sorted alphabetically by name within each group, and
  // firm groups sorted alphabetically by firm name. Picking an
  // association auto-syncs currentTenantId (see effect above).
  //
  // 2026-02-26 (Dave, client-scope pass) — client-scoped users (e.g.
  // an Association Admin like Chris) have no membership on any tenant
  // and so `/api/tenants` returns `[]`. They still get a `tenant_name`
  // on each row of `/api/associations` since the backend joins the
  // tenants table server-side. Prefer that inline name over the
  // `tenants` list lookup so the picker shows a real firm name instead
  // of a bare "Unassigned" section header.
  const associationGroups = React.useMemo(() => {
    const tenantName = (tid, fallback) =>
      tenants.find((t) => t.tenant_id === tid)?.name ||
      fallback ||
      "Unassigned";
    const byTenant = new Map();
    for (const a of associations) {
      const key = a.tenant_id || "__none__";
      if (!byTenant.has(key)) byTenant.set(key, []);
      byTenant.get(key).push(a);
    }
    const groups = [];
    for (const [tid, list] of byTenant.entries()) {
      const sorted = [...list].sort((x, y) =>
        (x.name || "").localeCompare(y.name || "")
      );
      groups.push({
        tenant_id: tid,
        name: tenantName(tid, sorted[0]?.tenant_name),
        items: sorted,
      });
    }
    groups.sort((x, y) => x.name.localeCompare(y.name));
    return groups;
  }, [associations, tenants]);

  React.useEffect(() => {
    if (!currentAid) return;
    // Wait for the initial associations fetch before considering the
    // "no accessible associations" case; otherwise we'd briefly clear
    // currentAid on first paint.
    if (!associationsLoaded) return;
    if (associations.length === 0) {
      onSelect("");
      return;
    }
    // If the current aid isn't in the user's accessible list at all,
    // clear it so downstream screens don't fire 403s.
    if (!associations.some((a) => a.aid === currentAid)) {
      onSelect("");
    }
  }, [associations, currentAid, onSelect, associationsLoaded]);

  // Slice E5n (2026-02-09) — Firms/Users/Audit collapsed under a single
  // "Admin" tab that opens a dropdown. Admin is always shown for logged-
  // in users; the dropdown filters items per credential (viewers see
  // "None"). The tab highlights as active whenever the Admin landing OR
  // any of its sub-screens is current.
  const NAV = user ? [...visibleNav, "Admin"] : visibleNav;

  return (
    <header
      className="border-b border-rule bg-white"
      data-testid="top-bar"
      /* 2026-02-15 (Dave — User pill hidden behind Elements/Contributions
         sticky thead) — the User pill's stacking context (z-1 on
         `.scope-strip`, z-2 on `<nav>`) competes with the sticky table
         headers on the Elements + Contributions screens
         (`position: sticky; z-index: 1..2`). Because <main> is a
         later DOM sibling than <header>, tied z-indexes tipped
         painting order in favour of <main>, hiding the pill behind
         the thead. Fix: promote the entire top-bar stacking context
         to z-30 so every child of the header — including the pill
         and the Admin dropdown — paints above any sticky thead
         inside <main>. Modals (z-50, z-60) still paint above the
         top-bar as intended. */
      style={{ position: "relative", zIndex: 30 }}
    >
      {/* Single grid with 3 columns × 2 rows. The middle column is shared:
          Row 1 = main nav, Row 2 = scope strip (Firm · Association · User).
          Because they share the column, the scope strip lines up EXACTLY
          under the tab names, not just the viewport. */}
      <div
        className="w-full pt-2 pb-1.5"
        style={{
          // 2026-02-28 (Dave) — vertical padding trimmed 8/6 → 4/4 so
          // this row is ~6 px shorter overall.  Enough to eliminate
          // the outer vertical scrollbar at 1440×900 without touching
          // font size or child spacing.
          // 2026-02-28 (Dave, v2) — trimmed further to 2/2 for another
          // 4 px; combined with the smaller association name below
          // this row is now noticeably shorter.
          padding: "2px var(--u-gutter) 2px",
          display: "grid",
          /* 2026-02-16 (Dave, revision 3) — grid columns changed from
             `auto minmax(0, 1fr) auto` to `1fr 1fr 1fr` so the middle
             column has its center at the viewport center at EVERY
             viewport size (matches the dashboard's own `grid-cols-3`
             layout). Previous variants relied on a hardcoded
             translateX(14px) tuned for a single viewport — which broke
             visibly on Dave's MacBook where the effective width was
             different from the test environment. With equal columns:
               • Logo lives in col 1 (justify-self:start, empty space
                 to its right)
               • Nav + pill strip live in col 2 (justify-self:center
                 → naturally over the dashboard's middle column)
               • Horizon selector lives in col 3 (justify-self:end,
                 empty space to its left)
             The 255-px spacer that used to reserve room when the
             horizon was hidden is no longer needed — col 3 is a
             full 1/3 of the viewport whether populated or not. */
          gridTemplateColumns: "1fr 1fr 1fr",
          gridTemplateRows: "auto auto",
          columnGap: "clamp(12px, 1.4vw, 24px)",
          // 2026-02-28 (Dave) — rowGap collapsed 4 → 0 so Row 2
          // (Association name + user pill) sits tighter against
          // Row 1 (nav + horizon) — pulls that pair up ~4 px into
          // the whitespace above them.
          rowGap: "0px",
          alignItems: "center",
        }}
      >
        {/* Row 1 · Left — logo + DEV pill. Step 12 (2026-08-03) — logo
            shrinks fluidly to leave nav room on iPad landscape. */}
        <div className="flex items-center gap-3" data-testid="brand-logo" style={{ minWidth: 0 }}>
          <img
            src="/ureserve-logo.png"
            alt="uReserve Planning — Reserve Fund Planning Reimagined"
            /* Slice E5s (2026-02-09) — Dave asked for ~20% larger logo
               dropped a few pixels. Height bumped from
               clamp(34px, 2.5vw, 42px) → clamp(41px, 3vw, 50px) and
               nudged down 4px via marginTop.
               2026-03-02 (Dave) — trimmed 15% (41→35 / 3vw→2.55vw /
               50→43) and dropped an extra 5 px (marginTop 14→19).
               2026-03-02 (Dave, later) — nudged 6 px right so the
               logo's left edge lines up with the Dashboard's Elements
               panel border, and dropped 10 more px (marginTop 19→29). */
            style={{ height: "clamp(35px, 2.55vw, 43px)", width: "auto", marginTop: 29, marginLeft: 6 }}
            data-testid="brand-logo-img"
          />
          {devMode && (
            <button
              onClick={onExitDevMode}
              title="Admin / dev mode active — click to exit"
              className="group border border-navy px-1.5 py-0.5 text-[9px] font-mono tracking-wider uppercase text-navy bg-navy-wash hover:bg-navy hover:text-white transition-colors cursor-pointer flex items-center gap-1"
              data-testid="dev-mode-badge"
            >
              <span>Dev</span>
              <span className="opacity-60 group-hover:opacity-100">×</span>
            </button>
          )}
        </div>

        {/* Row 1 · Middle → SWAPPED to Row 2 (2026-03-02, Dave) — main
            nav array moved DOWN one row so the Association name owns
            the top-middle spot.  Horizon selector stays put in Row 1
            right and the logo stays put in Row 1 left.  Grid
            placement forced via `gridRow: 2 / gridColumn: 2` so DOM
            order stays natural for keyboard/AT navigation. */}
        <nav
          className="flex items-center justify-center"
          data-testid="main-nav"
          style={{
            gap: "var(--u-nav-gap)",
            minWidth: 0,
            position: "relative",
            zIndex: 2,
            gridRow: 2,
            gridColumn: 2,
            transform: "translateY(-5px)",
          }}
        >
          {NAV.map((label) => {
            if (label === "Admin") {
              // 2026-02-23 (Dave) — dropdown collapsed. Admin is now a
              // plain nav link that routes to the Admin landing (which
              // renders the tile grid). Viewers with no admin access
              // still see the label grayed out and non-clickable.
              const active =
                currentScreen === "Admin" ||
                ADMIN_SUBSCREENS.includes(currentScreen);
              const canFirms = isPlatformAdmin(user);
              const canUsersAudit = isPlatformOrFirmAdmin(user);
              const hasAnyAdminAccess = canFirms || canUsersAudit || isAnyUserAdmin(user);
              if (!hasAnyAdminAccess) {
                return (
                  <span
                    key="Admin"
                    className="nav-link"
                    data-testid="nav-admin"
                    aria-disabled="true"
                    style={{
                      cursor: "default",
                      color: "#B0B7BE",
                      pointerEvents: "none",
                    }}
                  >
                    Admin
                  </span>
                );
              }
              return (
                <a
                  key="Admin"
                  onClick={() => onScreenChange("Admin")}
                  className={"nav-link " + (active ? "active" : "")}
                  data-testid="nav-admin"
                  style={{ cursor: "pointer" }}
                >
                  Admin
                </a>
              );
            }
            const active = currentScreen === label;
            return (
              <a
                key={label}
                onClick={() => onScreenChange(label)}
                className={"nav-link " + (active ? "active" : "")}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{ cursor: "pointer", whiteSpace: "nowrap" }}
              >
                {label === "Elements" && elementCount != null ? `Elements (${elementCount})` : label}
              </a>
            );
          })}
        </nav>

        {/* Row 1 · Right — "Horizon" label + horizon toggles (logout moved to Row 2).
            Step 12 (2026-08-03) — the "Horizon" label hides below 1280px
            to keep the whole nav row on one line at iPad landscape.
            2026-02-16 (Dave) — hide entirely on admin subscreens (Users,
            Firms, Audit) and on Support where horizon has no meaning.

            2026-02-16 (Dave, revision 2) — the "hidden" case originally
            rendered an empty spacer, which collapsed the grid's right
            column to 0 width. That in turn shifted the middle column's
            center ~125 px to the right, throwing the nav + pill row off
            centre on those screens. Fix: the spacer now reserves the
            SAME width the visible horizon cluster occupies (~240 px),
            so grid geometry stays constant across every screen and the
            middle column's center remains locked to the viewport
            centre.

            2026-02-28 (Dave) — Association + Elements tabs added to the
            hidden list: those screens don't consume the horizon
            variable (Association shows scenario config, Elements shows
            an inventory list). Only the four projection-driven tabs
            (Dashboard, Projections, Contributions, Support) still need
            the selector. */}
        {(currentScreen === "Admin"
            || ADMIN_SUBSCREENS.includes(currentScreen)
            || currentScreen === "Support"
            || currentScreen === "Association"
            || currentScreen === "Elements") ? (
          <div className="justify-self-end" />
        ) : (
        <div className="flex items-center gap-2 justify-self-end" style={{ minWidth: 0, marginTop: 4 }}>
          {/* 2026-02-16 (Dave) — collapsed the two-line "Planning\nHorizon"
              label to a single word "Horizon" at a larger font (13 px,
              was 11), vertically centered with the button strip. Reads
              cleaner and reduces the horizontal footprint of the label
              from ~57 → ~50 px. */}
          <span
            className="font-bold uppercase tracking-[0.08em] text-grey mr-1 select-none hidden xl:inline"
            style={{ fontSize: 13, lineHeight: 1 }}
            data-testid="horizon-label"
          >
            Horizon
          </span>
          <div
            className="flex items-stretch border border-rule overflow-hidden rounded-[3px]"
            role="tablist"
            data-testid="horizon-selector"
          >
            {HORIZONS.map((h) => {
              const active = h === horizon;
              return (
                <button
                  key={h}
                  data-testid={`horizon-${h}`}
                  onClick={() => onHorizonChange(h)}
                  className="text-[10.5px] font-mono tabular-nums transition-colors border-r border-rule last:border-r-0"
                  style={{
                    /* 2026-02-16 (Dave) — shrink horizon buttons so the
                       full 6-button strip + label roughly matches the
                       uReserve logo footprint on the left.
                       2026-02-28 (Dave) — dialled one notch smaller
                       (30×22 → 27×19) and switched to the standard
                       tight 3-px corner radius (via the wrapper's
                       `rounded-[3px]` + `overflow-hidden`) so the
                       strip matches the rest of the app's button
                       language.  Font trimmed 11 → 10.5 px so the
                       "30y" label still centres in the smaller pill. */
                    padding: "3px 0",
                    width: 27,
                    textAlign: "center",
                    background: active ? "#1C4E80" : "#fff",
                    color: active ? "#fff" : "#5A6B75",
                    fontWeight: active ? 600 : 500,
                    cursor: active ? "default" : "pointer",
                  }}
                >
                  {h}y
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Row 2 · Left — Plan-Year Picker REMOVED 2026-09-27 (Dave).
            The pill duplicated the plan-year controls that live in the
            Association → Details panel (PlanYearsDrawer). Global
            `viewingPlanYearId` state is still wired in App.js so future
            read-only-archive views can re-enable this trigger cheaply.
            An empty <div> keeps the CSS-grid auto-flow slot occupied so
            the association-label + user-pill stay in their columns. */}
        <div style={{ gridRow: 2, gridColumn: 1 }} aria-hidden="true" />
        {/* Row 2 · Middle → SWAPPED to Row 1 (2026-03-02, Dave) —
            Association name owns the top-middle spot now.  Grid
            placement forced via `gridRow: 1 / gridColumn: 2` so DOM
            order stays natural.  translateY dropped to 0 since
            there's no longer a row above to snug against. */}
        <div
          className="flex items-center justify-center"
          style={{ position: "relative", zIndex: 1, minHeight: 24, gridRow: 1, gridColumn: 2 }}
          data-testid="association-label"
        >
          {(() => {
            const a = associations.find((x) => x.aid === currentAid);
            if (!a) {
              const text = !associationsLoaded
                ? "Loading…"
                : associations.length === 0
                  ? "No associations available"
                  : "Select an association from the User menu →";
              return (
                <span
                  className="text-[13px] font-medium text-slate italic truncate"
                  style={{ maxWidth: 560 }}
                  data-testid="association-label-empty"
                >
                  {text}
                </span>
              );
            }
            return (
              <div className="flex flex-col items-center min-w-0" style={{ position: "relative", transform: "translateY(-6px)" }}>
              <div className="flex items-center justify-center min-w-0">
              <span
                className="font-bold truncate"
                style={{
                  fontSize: 25,
                  lineHeight: 1.05,
                  color: "#1C4E80" /* navy */,
                  letterSpacing: "0.005em",
                  maxWidth: 720,
                }}
                data-testid="association-label-name"
                title={a.name}
              >
                {a.name}
              </span>
              </div>
              {currentPlanYear ? (
                <span
                  className="font-semibold whitespace-nowrap"
                  style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", fontSize: 18.75, lineHeight: 1.2, color: "#4B5563", letterSpacing: "0.04em", marginTop: -1 }}
                  data-testid="association-plan-year"
                >
                  Plan Year {currentPlanYear}
                </span>
              ) : null}
              </div>
            );
          })()}
        </div>

        {/* Row 2 · Right — User pill (2026-02-25, Dave). Right-aligned
            so its right edge lines up with the Reserve Summary
            (waterfall) panel's right border below — both share the
            page's right gutter.
            2026-03-02 (Dave) — On the Contributions tab, a fixed-width
            "Comparative Analysis" / "Detailed Data View" toggle pill
            sits immediately to the left of the identity pill.  Fixed
            160 px width covers both labels ("Comparative Analysis" is
            the wider of the two by 2 chars), so the pill never twitches
            when its state flips.  Hidden on every other screen so the
            top-right cluster stays tidy. */}
        <div
          className="justify-self-end flex items-center gap-2"
          style={{ transform: "translateY(-5px)", position: "relative", zIndex: 1 }}
        >
          {user && (
            <UserMenu
              user={user}
              role={role}
              roleStyle={roleStyle}
              onLogout={logout}
              associationGroups={associationGroups}
              currentAid={currentAid}
              onSelectAssociation={onSelect}
              associationsLoaded={associationsLoaded}
            />
          )}
        </div>
      </div>
      <PlatformSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </header>
  );
}


// ─── User dropdown menu ──────────────────────────────────────────────────
// Renders the identity chip (role dot + name + caret) as a button. On click
// it opens a small menu with Profile · Change password · Sign out. Profile
// and Change password are placeholders for Slice 7 / Profile screen — they
// render as "coming soon" so the anchor exists but does not confuse users.
function UserMenu({ user, role, roleStyle, onLogout, associationGroups, currentAid, onSelectAssociation, associationsLoaded }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const { refresh } = useAuth();
  // 2026-03-06 (Dave) — Default-association toggle moved out of the
  // Association tab's Details header and into this dropdown, one star
  // per row. `savingAid` = which association is mid-PATCH so we can
  // show a per-row spinner and prevent concurrent toggles. Clicking a
  // star is independent of clicking the row label: star sets/clears the
  // user's default preference (PATCH /users/me/preferences); row label
  // still switches the active association as before.
  //
  // 2026-03-06 (Dave, follow-up) — When the star SETS a new default
  // (outlined → filled) and that association isn't already the active
  // one, also switch the active context so users don't have to sign
  // out and back in for the change to take effect. Clearing a default
  // (filled → outlined) leaves the active context alone — the user is
  // just clearing a preference, not picking anywhere to go.
  const [savingAid, setSavingAid] = useState(null);
  const toggleDefault = async (aid, isCurrentlyDefault) => {
    if (savingAid) return;
    setSavingAid(aid);
    try {
      const newAid = isCurrentlyDefault ? null : aid;
      await axios.patch(`${API}/users/me/preferences`, {
        default_association_id: newAid,
      });
      await refresh();
      // Setting a NEW default also switches the active association so
      // the Dashboard / Elements / etc. reflect the new choice
      // immediately. Skip when it's already active or when clearing.
      if (newAid && newAid !== currentAid) {
        setOpen(false);
        onSelectAssociation?.(newAid);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your preference.");
    } finally {
      setSavingAid(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = user.full_name || user.email;
  const roleLabel = roleStyle?.label || role;
  // 2026-02-25 (Dave) — Association picker now lives inside this menu.
  // Flat list of every association the user is credentialed for,
  // grouped by firm. Picking any row implicitly sets the current firm
  // (via the tenant-sync effect in TopBar). Firm-switch shortcut
  // dropped — selecting an association already switches firm context.
  const groups = Array.isArray(associationGroups) ? associationGroups : [];
  const hasAny = groups.some((g) => (g.items || []).length > 0);

  return (
    <div className="relative" ref={wrapRef} data-testid="user-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={`${label} · ${roleLabel} · ${user.email}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-2 py-[3px] bg-white text-[11px] text-ink hover:border-navy transition-colors cursor-pointer rounded-[3px]"
        style={{ border: "0.5px solid #1C4E80" }}
        data-testid="user-identity"
      >
        <span
          className="inline-block w-2 h-2 rounded-full shrink-0"
          style={{ background: roleStyle?.dot || "#7B8797" }}
          data-testid="role-dot"
        />
        <span className="font-medium text-[11px] text-ink truncate max-w-[160px]">
          {label}
        </span>
        <ChevronDown size={11} className="text-slate shrink-0" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[260px] max-w-[340px] bg-white border border-rule shadow-md z-50 max-h-[70vh] overflow-y-auto"
          data-testid="user-menu-panel"
        >
          {/* Identity header */}
          <div className="px-3 py-2 border-b border-rule">
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ background: roleStyle?.dot || "#7B8797" }}
              />
              <span className="text-[12px] font-semibold text-ink truncate">{label}</span>
            </div>
            <div className="text-[10.5px] text-slate mt-0.5 truncate">{user.email}</div>
            <div className="text-[9.5px] tracking-[0.1em] uppercase font-semibold text-grey mt-1">
              {roleLabel}
            </div>
          </div>

          {/* 2026-02-25 (Dave) — Associations, grouped by firm.
              2026-02-26 (Dave, client-scope pass) — when a user only
              belongs to a single firm-group (very common for
              association-scoped board admins and single-firm analysts),
              the firm section header is redundant. Suppress it so the
              menu reads as a clean, flat association list. */}
          <div className="border-b border-rule" data-testid="user-menu-associations">
            <div className="px-3 pt-2 pb-1 flex items-center justify-between text-[9.5px] tracking-[0.1em] uppercase font-semibold text-slate">
              <span>Your Associations</span>
              {/* 2026-03-06 (Dave) — column header for the star toggles.
                  Right-padded so its baseline sits over the 5-px star
                  button on each row (row = px-3 + w-5 star). */}
              <span className="pr-[3px]" data-testid="user-menu-default-col-header">
                Default
              </span>
            </div>
            {!associationsLoaded ? (
              <div className="px-3 py-1.5 text-[11.5px] text-slate italic">Loading…</div>
            ) : !hasAny ? (
              <div className="px-3 py-1.5 text-[11.5px] text-slate italic">
                No associations available
              </div>
            ) : (
              (() => {
                const nonEmpty = groups.filter(
                  (g) => Array.isArray(g.items) && g.items.length > 0
                );
                const showFirmHeaders = nonEmpty.length > 1;
                return nonEmpty.map((g, gi) => (
                  <div key={g.tenant_id || gi} data-testid={`user-menu-firm-group-${g.tenant_id || gi}`}>
                    {showFirmHeaders && (
                      <div
                        className="px-3 py-1 bg-canvas text-[9.5px] tracking-[0.1em] uppercase font-semibold text-slate border-t border-rule"
                        data-testid={`user-menu-firm-header-${g.tenant_id || gi}`}
                      >
                        {g.name || "—"}
                      </div>
                    )}
                    {g.items.map((a) => {
                      const isActive = a.aid === currentAid;
                      const isDefault = a.aid === user?.default_association_id;
                      const isSaving = savingAid === a.aid;
                      return (
                        <div
                          key={a.aid}
                          className={
                            "w-full flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] " +
                            (isActive ? "bg-navy-wash/40" : "")
                          }
                          data-testid={`user-menu-assoc-row-${a.aid}`}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setOpen(false);
                              onSelectAssociation?.(a.aid);
                            }}
                            className={
                              "flex-1 min-w-0 flex items-center gap-1.5 text-left truncate hover:text-coral " +
                              (isActive ? "text-navy font-semibold" : "text-navy")
                            }
                            data-testid={`user-menu-assoc-${a.aid}`}
                            title={`ID: ${a.tenant_display_id || "—"} · ${a.name}${isDefault ? " · your default" : ""}`}
                          >
                            <span
                              className="inline-block w-3 shrink-0 text-navy"
                              aria-hidden="true"
                            >
                              {isActive ? "✓" : ""}
                            </span>
                            <span className="truncate">
                              {`ID: ${a.tenant_display_id || "—"} · ${a.name}`}
                            </span>
                          </button>
                          {/* 2026-03-06 (Dave) — Star column. Filled coral =
                              default; outlined navy = click to make default.
                              Click is independent of the row-select button
                              above so the menu stays open when toggling. */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDefault(a.aid, isDefault);
                            }}
                            disabled={isSaving}
                            className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-navy-wash disabled:opacity-60"
                            title={
                              isDefault
                                ? "Currently your default — click to clear"
                                : "Open this association by default on every login"
                            }
                            data-testid={`user-menu-assoc-default-toggle-${a.aid}`}
                            aria-label={isDefault ? "Clear default association" : "Set as default association"}
                            aria-pressed={isDefault}
                          >
                            {isSaving ? (
                              <Loader2 size={11} className="animate-spin text-slate" />
                            ) : (
                              <Star
                                size={12}
                                strokeWidth={2}
                                fill={isDefault ? "#EA6A47" : "none"}
                                stroke={isDefault ? "#EA6A47" : "#1C4E80"}
                                data-testid={isDefault ? `user-menu-assoc-default-${a.aid}` : undefined}
                              />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()
            )}
          </div>

          {/* Actions */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new CustomEvent("ureserve:open-edit-name"));
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-ink hover:bg-canvas text-left"
            data-testid="user-menu-profile"
          >
            <UserIcon size={13} />
            <span className="flex-1">Edit name</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new CustomEvent("ureserve:open-change-password"));
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-ink hover:bg-canvas text-left"
            data-testid="user-menu-change-password"
          >
            <KeyRound size={13} />
            <span className="flex-1">Change password</span>
          </button>

          <div className="border-t border-rule" />

          <button
            type="button"
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-ink hover:bg-coral-wash hover:text-coral transition-colors text-left"
            data-testid="user-menu-signout"
          >
            <LogOut size={13} />
            <span className="flex-1">Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}


// ─── Slice E6h · Standard-template pill dropdown ─────────────────────────
// Replaces the native <select> in the scope strip so the Development
// (tenant) and Association pickers render the same design language as the
// rest of the app: navy-bordered pill, chevron affordance, and a menu
// panel with `border-rule bg-white shadow-lg rounded-[3px]`. Items get
// navy text on white with a `navy-wash` hover, matching the +Element and
// Admin dropdowns.
function PillDropdown({ testid, Icon, label, items, groups, onPick, minWidth, maxWidth, disabled, emptyText }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // 2026-02-25 (Dave) — `groups` (if provided) supersedes `items` so
  // the picker can render firm-scoped section headers. Falls back to
  // the flat `items` array for existing callers.
  const hasGroups = Array.isArray(groups) && groups.length > 0
    && groups.some((g) => Array.isArray(g.items) && g.items.length > 0);
  const hasItems = !hasGroups && Array.isArray(items) && items.length > 0;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        data-testid={testid}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 border border-navy bg-white text-ink px-2 py-0.5 text-[11.5px] font-medium truncate rounded-[3px] hover:bg-navy-wash disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ minWidth: minWidth || undefined, maxWidth: maxWidth || undefined }}
        title={label}
      >
        {Icon && <Icon size={11} className="text-slate shrink-0" />}
        <span className="truncate flex-1 text-left">{label}</span>
        <ChevronDown size={11} className="text-slate shrink-0" />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`${testid}-menu`}
          className="absolute left-0 mt-1 min-w-full bg-white border border-rule shadow-lg z-50 rounded-[3px] overflow-hidden max-h-[60vh] overflow-y-auto"
          style={{ maxWidth: 360, minWidth: 220 }}
        >
          {hasGroups ? (
            groups.map((g, gi) => (
              (Array.isArray(g.items) && g.items.length > 0) ? (
                <div key={g.key || g.name || gi} data-testid={`${testid}-group-${g.key || gi}`}>
                  <div
                    className="px-3 py-1 bg-canvas text-[9.5px] tracking-[0.1em] uppercase font-semibold text-slate border-t border-rule first:border-t-0"
                    data-testid={`${testid}-group-header-${g.key || gi}`}
                  >
                    {g.name || "—"}
                  </div>
                  {g.items.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      role="menuitem"
                      onClick={() => { setOpen(false); onPick(it.id); }}
                      data-testid={`${testid}-item-${it.id}`}
                      className={
                        "w-full text-left px-3 py-1.5 text-[11.5px] hover:bg-navy-wash truncate " +
                        (it.active ? "text-navy font-semibold bg-navy-wash/40" : "text-navy")
                      }
                      title={it.label}
                    >
                      {it.label}
                    </button>
                  ))}
                </div>
              ) : null
            ))
          ) : !hasItems ? (
            <div className="px-3 py-1.5 text-[11.5px] text-slate italic">
              {emptyText || "No options"}
            </div>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onPick(it.id); }}
                data-testid={`${testid}-item-${it.id}`}
                className={
                  "w-full text-left px-3 py-1.5 text-[11.5px] hover:bg-navy-wash truncate " +
                  (it.active ? "text-navy font-semibold bg-navy-wash/40" : "text-navy")
                }
                title={it.label}
              >
                {it.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}



// ─── PlanYearPicker (Slice B · Dave 2026-02-17) ──────────────────────────
// Compact pill dropdown showing every plan year for the current
// association. The active year sits at the top with a coral dot; every
// archived year below is labelled "· Closed". Selecting a non-active
// year triggers `onPick(planYearId)` which App.js turns into a global
// `viewingPlanYearId` — the app's axios interceptor then transparently
// re-scopes every subsequent read to that year.
//
// Selecting the active year passes `null` back up, clearing the view
// override and returning to normal behavior.
//
// Refreshes on `refresh-plan-years` window event so a reactivate
// elsewhere (e.g. PlanYearsDrawer) instantly updates this pill too.
function PlanYearPicker({ aid, viewingPlanYearId, onPick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!aid) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/associations/${aid}/plan-years`);
      setRows(r.data?.plan_years || []);
    } catch { /* silent — pill just shows Loading… */ } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("refresh-plan-years", onRefresh);
    return () => window.removeEventListener("refresh-plan-years", onRefresh);
  }, [aid]);

  const active = rows.find((r) => r.status === "active");
  const activeId = active?.plan_year_id || null;

  // 2026-09-27 (Dave) — Sync-year guard. If the app-level
  // `viewingPlanYearId` names a plan year that is NOT in the freshly
  // fetched rows for this association (e.g. it was hard-deleted, or
  // this association was recently swapped and the id belonged to a
  // different one), transparently clear it back to the active year.
  // Without this the axios interceptor would keep stamping a stale
  // `plan_year_id` onto every bundle read → Association tab shows the
  // wrong year while the picker (which falls back to `activeId`)
  // correctly shows the current active. Guarded by `rows.length > 0`
  // so we don't clear during the initial load.
  useEffect(() => {
    if (rows.length === 0) return;
    if (!viewingPlanYearId) return;
    const stillExists = rows.some((r) => r.plan_year_id === viewingPlanYearId);
    if (!stillExists) onPick(null);
  }, [rows, viewingPlanYearId, onPick]);

  const currentId = viewingPlanYearId || activeId;
  const currentRow = rows.find((r) => r.plan_year_id === currentId) || active;

  const label = currentRow
    ? `Plan Year ${currentRow.year}${currentRow.status === "active" ? "" : " · Closed"}`
    : (loading ? "Loading…" : "No plan year");

  // Order: active first, then archived years descending by year.
  const ordered = useMemo(() => {
    const a = rows.filter((r) => r.status === "active");
    const archived = rows
      .filter((r) => r.status !== "active")
      .sort((x, y) => (y.year || 0) - (x.year || 0));
    return [...a, ...archived];
  }, [rows]);

  return (
    <PillDropdown
      testid="plan-year-picker"
      Icon={CalendarClock}
      label={label}
      minWidth={170}
      maxWidth={170}
      disabled={rows.length === 0 && !loading}
      items={ordered.map((py) => ({
        id: py.plan_year_id,
        // Keep the label short; the coral-dot on the active row is
        // rendered inline via the PillDropdown item map — but our
        // PillDropdown doesn't support per-item icons, so we suffix a
        // text tag instead.
        label:
          py.status === "active"
            ? `Plan Year ${py.year} · Active`
            : `Plan Year ${py.year} · Closed`,
        active: py.plan_year_id === currentId,
      }))}
      onPick={(id) => {
        // Selecting the active PY clears the view override; selecting
        // any other year sets it.
        onPick(id === activeId ? null : id);
      }}
    />
  );
}
